import { supabase } from '../supabase';
import { Recommendation, Game, RecommendationAlgorithm } from '../../types';
import { CacheService } from './cacheService';
import { RecommendationStorage } from './recommendationStorage';
import { RecommendationMonitor } from './recommendationMonitor';

// TODO: Considerar mover getAllFeedbackGameIds e clearCacheAfterCollectionChange para cá se fizer sentido.

export class RecommendationFeedbackService {
  constructor(
    private storage: RecommendationStorage = new RecommendationStorage(),
    private monitor: RecommendationMonitor = new RecommendationMonitor(),
    private cacheService: CacheService = new CacheService() // Adicionado para clearCache
  ) {}

  /**
   * Dá feedback (curtiu/não curtiu) para uma recomendação específica.
   * Atualiza o estado no RecommendationStorage e invalida o cache.
   * Permite opcionalmente fornecer uma recomendação temporária se ela ainda não existe no banco.
   * @param recommendationId O ID da recomendação.
   * @param liked O estado do feedback: true para curtiu, false para não curtiu, null para remover feedback.
   * @param temporaryRecommendation Dados da recomendação temporária (opcional).
   * @returns A recomendação atualizada ou a temporária salva, ou null em caso de erro.
   */
  public async giveRecommendationFeedback(
    recommendationId: string,
    liked: boolean | null,
    temporaryRecommendation?: Recommendation | null
  ): Promise<Recommendation | null> {
    try {
      console.log(`[FeedbackService] Processing feedback for recommendation ${recommendationId}: liked=${liked}`);
      let updatedRec = await this.storage.updateRecommendationFeedback(recommendationId, liked);

      if (updatedRec) {
        console.log(`[FeedbackService] Feedback for recommendation ${recommendationId} saved successfully via direct update in DB.`);

        if (liked === true) {
          console.log(`[FeedbackService] Tracking click for liked recommendation ${recommendationId}.`);
          this.monitor.trackClick(updatedRec as any); // Cast para any para compatibilidade com trackClick
        }

        const userId = updatedRec.user_id;
        if (userId) {
          try {
            console.log(`[FeedbackService] Invalidando cache após feedback para o usuário ${userId}`);
            // Usar o método clearCacheAfterCollectionChange que será movido ou reimplementado aqui
            await this.clearCacheAfterFeedback(userId); 
          } catch (cacheError) {
            console.error(`[FeedbackService] Failed to invalidate cache for user ${userId}:`, cacheError);
          }
        } else {
          console.warn(`[FeedbackService] Could not invalidate cache because userId is missing from updated recommendation ${recommendationId}.`);
        }
        return updatedRec;

      } else if (temporaryRecommendation) {
         console.log(`[FeedbackService] Recommendation ${recommendationId} not found in DB, attempting to save temporary recommendation provided.`);
         
         const tempRec = {
            ...temporaryRecommendation,
            id: recommendationId, 
            liked: liked, 
            updated_at: new Date().toISOString(),
            viewed: true // Dar feedback implica visualização
         };
         
         console.log(`[FeedbackService] Attempting to save temporary recommendation ${recommendationId} to DB...`);
         
         try {
            const { data, error } = await supabase
              .from('recommendations')
              .upsert([tempRec]) 
              .select()
              .single(); 
              
            if (error) {
              console.error(`[FeedbackService] Error saving temporary recommendation ${recommendationId} to DB:`, error);
              return null; 
            }
            
            if (data) {
              updatedRec = data as unknown as Recommendation; 
              console.log(`[FeedbackService] Temporary recommendation ${recommendationId} saved successfully to DB.`);
              
              const userIdForCache = tempRec.user_id;
              if (userIdForCache) {
                try {
                  console.log(`[FeedbackService] Invalidando cache após salvar recomendação temporária para o usuário ${userIdForCache}`);
                  await this.clearCacheAfterFeedback(userIdForCache);
                } catch (cacheError) {
                  console.error(`[FeedbackService] Failed to invalidate cache for user ${userIdForCache} after temp save:`, cacheError);
                }
              }
              
              if (liked === true) {
                console.log(`[FeedbackService] Tracking click for liked temporary recommendation ${recommendationId}.`);
                this.monitor.trackClick(updatedRec as any);
              }
              
              return updatedRec;
            } else {
              console.warn(`[FeedbackService] No data returned from DB after saving temporary recommendation ${recommendationId}. Upsert might have been a no-op or failed silently.`);
              return null; 
            }
         } catch (saveError) {
            console.error(`[FeedbackService] Exception when saving temporary recommendation ${recommendationId}:`, saveError);
            return null; 
         }
      } else {
         console.warn(`[FeedbackService] Recommendation ${recommendationId} not found in DB and no temporary data provided. Cannot process feedback.`);
         return null;
      }
    } catch (error) {
      console.error(`[FeedbackService] Erro ao processar feedback para recomendação ${recommendationId}:`, error);
      return null;
    }
  }

  /**
   * Retorna as recomendações com feedback (curtidas ou não curtidas) de um usuário.
   * @param filters Filtros para obter recomendações com feedback (userId e liked).
   * @returns Lista de recomendações com feedback.
   */
  async getFeedbackRecommendations(filters: {userId?: string; liked?: boolean} = {}): Promise<Recommendation[]> {
    try {
      const { userId, liked = true } = filters; 
      if (!userId) {
        console.error('[FeedbackService] getFeedbackRecommendations: userId é obrigatório.');
        return [];
      }
      
      console.log(`[FeedbackService] Fetching feedback recommendations for user ${userId} (liked: ${liked})`);

      // A coluna 'game' na tabela 'recommendations' é JSONB, não uma FK direta para join aqui.
      // Selecionamos todos os campos da recomendação, incluindo o objeto 'game' embutido.
      const { data, error } = await supabase
        .from('recommendations')
        .select('*') // Seleciona todas as colunas, incluindo o JSONB 'game'
        .eq('user_id', userId)
        .eq('liked', liked) 
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[FeedbackService] Error fetching feedback recommendations from Supabase:', error);
        return [];
      }
      if (!data) {
        console.log('[FeedbackService] No feedback recommendations found from Supabase.');
        return [];
      }
      
      // Os dados já devem vir com o objeto 'game' embutido corretamente se o tipo no Supabase for JSONB
      // e se o objeto 'game' foi salvo corretamente durante a criação da recomendação.
      // Vamos garantir que o campo 'game' exista e seja um objeto.
      const recommendationsWithGames = data.filter(rec => rec.game && typeof rec.game === 'object').map(rec => {
        // Certificar de que o objeto game tem os campos esperados por Recommendation -> Game
        // Isso pode exigir uma transformação ou validação mais robusta se a estrutura do JSONB 'game' variar.
        return {
          ...rec,
          game: rec.game as Game // Fazendo um cast, mas idealmente validaríamos a estrutura.
        } as Recommendation;
      });

      console.log(`[FeedbackService] Fetched ${recommendationsWithGames.length} valid feedback recommendations.`);
      return recommendationsWithGames;
    } catch (err) {
      console.error('[FeedbackService] Error processing feedback recommendations:', err);
      return [];
    }
  }

  /**
   * Método privado para limpar o cache após feedback.
   * @param userId ID do usuário
   */
  private async clearCacheAfterFeedback(userId: string): Promise<void> {
    try {
      if (!userId) return;
      
      const recCacheKey = `recommendations:${userId}:default`;
      const forceRecCacheKey = `recommendations:${userId}:force`;
      
      console.log(`[FeedbackService] Invalidando cache (chaves: ${recCacheKey}, ${forceRecCacheKey}) para o usuário ${userId} após feedback.`);
      
      await this.cacheService.delete(recCacheKey);
      await this.cacheService.delete(forceRecCacheKey);
      
      console.log(`[FeedbackService] Cache invalidado com sucesso para o usuário ${userId} após feedback.`);
    } catch (error) {
      console.error(`[FeedbackService] Erro ao limpar cache após feedback para user ${userId}: ${error}`);
    }
  }
} 